import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Trash2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Generate time options in 15-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h24 = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      options.push({
        value: `${h24}:${m}:00`,
        label: `${h12}:${m.padStart(2, '0')} ${ampm}`,
      });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

interface TimeRangePickerProps {
  startTime: string;
  endTime: string;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onRemove?: () => void;
  showRemove?: boolean;
  disabled?: boolean;
  className?: string;
}

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  onRemove,
  showRemove = false,
  disabled = false,
  className,
}) => {
  // Validate that end time is after start time
  const isValid = startTime < endTime;
  const hasError = startTime && endTime && !isValid;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select value={startTime} onValueChange={onStartTimeChange} disabled={disabled}>
        <SelectTrigger className={cn('w-[120px]', hasError && 'border-red-300')}>
          <SelectValue placeholder="Start" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {TIME_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-slate-400 text-sm">to</span>

      <Select value={endTime} onValueChange={onEndTimeChange} disabled={disabled}>
        <SelectTrigger className={cn('w-[120px]', hasError && 'border-red-300')}>
          <SelectValue placeholder="End" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {TIME_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasError && (
        <div className="flex items-center text-red-500 text-sm">
          <AlertCircle className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Invalid range</span>
        </div>
      )}

      {showRemove && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={disabled}
          className="h-8 w-8 text-slate-400 hover:text-red-500"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default TimeRangePicker;







